import { useCallback, useEffect, useState } from 'react';
import { useLocker } from '../../../../context/hardware';
import { useKioskImage, useKioskState } from '../../../../context/Kiosk/kiosk';
import { Spinner } from '../../../../controls/Spinner/Spinner';
import { GMDialogRetryCancel } from '../../../../controls/Dialog';
import { ScrollableBody } from '../../../Layout/ScrollableBody/ScrollableBody';
import { KioskImageDisplay } from '../../../../controls/KioskImageDisplay';
import { GMButtonGroup } from '../../../../controls/ButtonGroup';
import { GMRoundedIcon, GMSizedIcon } from '../../../../controls/Icon';
import { Heading } from '../../../../controls/Heading/Heading';
import { useAdminDispatch } from '../../../../context/Admin/admin';
import { LockerOpenProps } from './LockerOpen.types';
import { CTAButton } from '../../../../controls/ButtonBase';
import { useLocalization } from '../../../../context/Localization/localization';
import { AdminDispatchState, AdminDispatchTypes } from '../../../../context/Admin/admin.types';
import { LocalizationType } from '../../../../context/Localization/localization.types';
import { useAppointmentStateUnsafe, useLockerOperation } from '../../../../context/Appointment/appointment';
import { IAppointment } from '../../../../API/GoMoto';
import { KeyDropError } from '../../../../context/Appointment/appointment.types';
import { useAdminLockerOperationState } from '../../../../context/AdminLockerOperation/adminLockerOperation';

/**
 * The `LockerOpen` component is a screen that handles an admin locker operation to either open the locker
 * without triggering appointment responses, or retrieve/return the key stored in the locker.
 *
 * @param onProceed - {@link LockerOpenProps.onProceed onProceed} description.
 */
export const LockerOpen = ({
    onProceed,
}: LockerOpenProps) => {
    const Localization: LocalizationType = useLocalization();
    const adminDispatch: AdminDispatchState = useAdminDispatch();
    const appointment: IAppointment | undefined = useAppointmentStateUnsafe();
    const { lockersEnabled, error: lockerError } = useLocker();
    const { kioskType } = useKioskState();
    const { start, locker, error: keyError, complete, hasOpened, forceLockerClose } = useLockerOperation({ byAdvisor: true });
    const { lockerOpType, lockerId } = useAdminLockerOperationState();
    const keyDropImage: string = useKioskImage('KeyDrop');
    const kioskImage: string = useKioskImage('KeyDropWarning');

    const [error, setError] = useState<string | undefined>(lockerError);
    const [keySlotNumber] = useState<number>(lockerId ?? (appointment && appointment.keySlotNumber ? appointment.keySlotNumber : -1));

    const lockerClosed: VoidFunction = useCallback(async () => {
        if (!complete) {
            await forceLockerClose();
        }
        onProceed();
    }, [complete, forceLockerClose, onProceed]);

    useEffect(() => {
        start({
            lockerAction: lockerOpType,
            manual: lockerOpType !== 'open-locker',
            lockerId: lockerId !== undefined ? lockerId : undefined,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!lockersEnabled || complete) {
            onProceed();
        }
    }, [lockersEnabled, onProceed, complete]);

    useEffect(() => {
        if (keyError !== KeyDropError.none) {
            setError(Localization.FailedToOpenLocker);
        }
    }, [keyError, Localization.FailedToOpenLocker]);

    useEffect(() => {
        if (complete && !!locker && lockerOpType !== 'open-locker') {
            adminDispatch({
                type: AdminDispatchTypes.SetLockerChanged,
                payload: locker,
            });
        }
    }, [adminDispatch, complete, locker, lockerOpType]);

    const onRetryOpenLocker = async () => {
        setError(undefined);
        start({
            lockerAction: lockerOpType,
            manual: lockerOpType !== 'open-locker',
            lockerId: lockerId !== undefined ? lockerId : undefined,
        });
    };

    if (!lockersEnabled) {
        return (
            <Spinner />
        );
    }
    else {
        return (
            <ScrollableBody
                heading={
                    <Heading
                        primaryHeading={Localization.LockerDoorIsOpen}
                        secondaryHeading={Localization.formatString(Localization.LockerNumIsOpen, keySlotNumber)}
                        roundedIcon={
                            <GMRoundedIcon roundedBackgroundSize={'large'} themeType={'secondary'}>
                                <GMSizedIcon iconType={'Key'} iconSize={'large'} />
                            </GMRoundedIcon>
                        }
                    />
                }
                proceedButtons={
                    <GMButtonGroup
                        buttons={[
                            <CTAButton
                                onClick={lockerClosed}
                                styleType={'primary'}
                                label={Localization.LockerClosed}
                                kioskOptions={{ inlineOptions: { sizedIcon: <GMSizedIcon iconType={'Complete'} ariaLabel={Localization.CompleteCheckmarkIcon} /> } }}
                                id={Localization.LockerClosed}
                                ariaLabel={Localization.LockerClosed}
                                disabled={kioskType === 'express' && !hasOpened}
                            />,
                        ]}
                    />
                }
            >
                <KioskImageDisplay
                    left={{
                        image: keyDropImage,
                    }}
                    right={{
                        image: kioskImage,
                        bottom: Localization.AfterClosingTheLocker,
                    }}
                />
                <GMDialogRetryCancel
                    ariaLabel={Localization.Retry}
                    open={!!keyError}
                    onCancel={() => {
                        onProceed();
                    }}
                    onRetry={onRetryOpenLocker}
                >
                    {error}
                </GMDialogRetryCancel>
            </ScrollableBody>
        );
    }

};

